import React from 'react';
import { motion } from 'framer-motion';
import { SunIcon, PaletteIcon, LampDeskIcon, LayoutIcon, GemIcon } from 'lucide-react';
const specs = [{
  icon: GemIcon,
  title: 'Design Concept',
  description: 'Quiet luxury meets Japandi calm — minimal visual noise, warm layered lighting, natural materials with tactile depth.'
}, {
  icon: PaletteIcon,
  title: 'Interior Palette',
  description: 'Warm off-white stone ivory, light oak & ash veneer, matte black detailing, taupe and sand soft furnishings.'
}, {
  icon: LampDeskIcon,
  title: 'Lighting System',
  description: 'Layered ambient 2700K warm white — under-cabinet strips, bed base glow, concealed ceiling perimeter, fibre-optic starlight option.'
}, {
  icon: LayoutIcon,
  title: 'Layout Configuration',
  description: 'Front swivel lounge seats, slim side kitchen module, opposing U-bench seating, rear fixed convertible U-bed sleeping system.'
}, {
  icon: SunIcon,
  title: 'Signature Features',
  description: 'Handleless push-to-open cabinetry, hidden appliance integration, floating furniture effect, seamless surface transitions.'
}];
export function NoirAtelierSection() {
  return <section id="noir-atelier" className="relative w-full bg-dark-900 py-24 md:py-36 overflow-hidden" data-id="element-106">
      {/* Subtle warm glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-gold/[0.03] rounded-full blur-[120px]" data-id="element-107" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10" data-id="element-108">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-8" data-id="element-109">
          <p className="text-gold/60 text-xs font-semibold tracking-[0.5em] uppercase mb-6 font-body" data-id="element-110">
            Flagship Conversion
          </p>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4" data-id="element-111">
            PV5 <span className="text-gold" data-id="element-112">NOIR</span> ATELIER
          </h2>
          <p className="font-display text-xl sm:text-2xl text-white/40 italic" data-id="element-113">
            Designed to Live Beautifully.
          </p>
        </motion.div>

        {/* Divider line */}
        <motion.div initial={{
        scaleX: 0
      }} whileInView={{
        scaleX: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.3
      }} className="w-32 h-px bg-gold/40 mx-auto mb-16" data-id="element-114" />

        {/* Hero image */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative rounded-2xl overflow-hidden mb-20 aspect-[21/9]" data-id="element-115">
          <img src="https://cdn.magicpatterns.com/uploads/sZVjrUyD2zp5HNuMCu6CBm/WhatsApp_Image_2026-04-03_at_12.38.52_AM.jpg" alt="Kia PV5 Noir Atelier with pop-top roof raised, rear three-quarter view in workshop" className="w-full h-full object-cover" data-id="element-116" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/30 to-transparent" data-id="element-117" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-900/40 to-transparent" data-id="element-118" />
          <div className="absolute bottom-8 left-8 md:bottom-12 md:left-12" data-id="element-119">
            <p className="text-gold text-xs tracking-[0.4em] uppercase font-body mb-2" data-id="element-120">
              Kia Campervans Ltd
            </p>
            <p className="text-white/60 text-sm font-body" data-id="element-121">
              Architectural · Minimal · Premium · Calm
            </p>
          </div>
        </motion.div>

        {/* Positioning statement */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.7
      }} className="max-w-3xl mx-auto text-center mb-20" data-id="element-122">
          <p className="text-white/50 text-base sm:text-lg leading-relaxed font-body" data-id="element-123">
            The Kia PV5 Noir Atelier redefines the campervan as a premium living
            space. It removes the visual language of traditional motorhomes and
            replaces it with the calm precision of modern architectural
            interiors.
          </p>
          <p className="text-white/30 text-base mt-4 font-body italic" data-id="element-124">
            This is not a travel vehicle. It is a mobile design space.
          </p>
        </motion.div>

        {/* Spec grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-125">
          {specs.map((spec, i) => <motion.div key={spec.title} initial={{
          opacity: 0,
          y: 25
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className={`relative p-8 rounded-xl border border-white/5 bg-dark-700/30 backdrop-blur-sm hover:border-gold/20 transition-all duration-500 ${i === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`} data-id="element-126">
              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center mb-5" data-id="element-127">
                <spec.icon className="text-gold" size={18} data-id="element-128" />
              </div>
              <h3 className="font-display text-lg font-semibold mb-3 tracking-wide" data-id="element-129">
                {spec.title}
              </h3>
              <p className="text-white/40 text-sm leading-relaxed font-body" data-id="element-130">
                {spec.description}
              </p>
            </motion.div>)}
        </div>

        {/* Material strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        delay: 0.3
      }} className="mt-16 p-8 rounded-xl border border-gold/10 bg-gold/[0.03]" data-id="element-131">
          <h4 className="font-display text-lg font-semibold text-gold mb-4" data-id="element-132">
            Material Specification
          </h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-133">
            {['Real wood veneer cabinetry', 'Soft-touch matte composite panels', 'Brushed black metal detailing', 'Solid surface countertop', 'Automotive-grade upholstery', 'Fabric wall panel inserts'].map(material => <div key={material} className="flex items-center gap-3" data-id="element-134">
                <div className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" data-id="element-135" />
                <span className="text-white/50 text-sm font-body" data-id="element-136">
                  {material}
                </span>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}